import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { useId } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import {
  ConfidenceLabel,
  ProposalPricebookMatchButton,
  PurchaseOrderMatchButton,
  StChangesNote,
} from "@/accounts-payable/common";
import type { ApAgentAction, ApDocumentType } from "@/accounts-payable/types";
import {
  actionTitle,
  approveLabel,
  documentTypeLabel,
  isNoOpAction,
  proposalLinkAriaLabel,
  purchaseOrderProposalLink,
} from "@/accounts-payable/utils";
import { DISMISS_ICON_BUTTON } from "./dismiss-icon-class";

const FIX_LINK = "text-xs whitespace-nowrap underline";

export const ActionRow = ({
  action,
  runActions,
  businessId,
  document,
  showDivider,
  resolving = false,
  onResolve,
}: {
  action: ApAgentAction;
  /** Every action of the action's run, to label a PO that creates new items. */
  runActions: ApAgentAction[];
  businessId: string | null;
  /**
   * The document the action is for. With it, a PO or pricebook proposal
   * offers choosing the PO or the line's item by hand instead, as the review
   * page does.
   */
  document?: {
    id: string;
    type: ApDocumentType;
    /** The vendor's document number, naming the row's links for screen readers. */
    number: string | null;
    /** The document's newest run id, to tell whether a pick queued a new one. */
    agentRunId: string | null;
    hasJob: boolean;
  };
  /** False for the first row — the band above it already draws that edge. */
  showDivider: boolean;
  resolving?: boolean;
  onResolve: (decision: "approved" | "rejected") => void;
}) => {
  const noOp = isNoOpAction(action);
  // The API's reason it would refuse the approval (e.g. a PO whose new
  // pricebook item was dismissed); the review card shows the same text.
  const blockedReason = action.approval_blocked_reason;
  const blockedReasonId = useId();
  const poLink = document
    ? purchaseOrderProposalLink(action, document.hasJob)
    : null;
  const kind = document ? documentTypeLabel(document.type).lower : "";
  // The way out when the proposal's target is wrong rather than its details:
  // the pick supersedes the proposal and has the agent re-check. A link on
  // the details line, so the Approve / ✕ column keeps its two edges.
  const fix =
    document && poLink ? (
      <PurchaseOrderMatchButton
        businessId={businessId}
        documentId={document.id}
        documentType={document.type}
        agentRunId={document.agentRunId}
        hasJob={poLink.hasJob}
        label={poLink.label}
        ariaLabel={proposalLinkAriaLabel(
          poLink.label,
          `choose the purchase order for ${kind} ${document.number ?? ""}`.trim(),
        )}
        appearance="link"
        className={FIX_LINK}
      />
    ) : document ? (
      <ProposalPricebookMatchButton
        businessId={businessId}
        documentId={document.id}
        documentType={document.type}
        documentNumber={document.number}
        agentRunId={document.agentRunId}
        action={action}
        appearance="link"
        className={FIX_LINK}
      />
    ) : null;
  return (
    <Stack align="start" gap={0} className="w-full">
      {/* On a phone the buttons go under the text rather than squeezing it
          into a narrow column beside them. */}
      <Stack
        direction="column"
        gap={2}
        align="stretch"
        justify="between"
        className={cn("w-full sm:flex-row sm:items-start sm:gap-4", {
          "border-t border-line": showDivider,
        })}
      >
        <Stack align="start" gap={0} className="min-w-0 sm:flex-1">
          <span className="text-sm">{actionTitle(action)}</span>
          {/* Traffic-light on the confidence figure so a scan down the column
            separates settled rows from the ones that repay a read: green ≥90%
            (bulk-approvable), amber 70–90%, red <70%. All semibold — at this
            size colour alone lets the green and amber recede. The % is
            always spelled out, so colour is never the only signal. */}
          {noOp ? (
            <Text variant="caption" tone="muted" className="mt-0.5">
              {action.rationale}
            </Text>
          ) : (
            // No separator: on a narrow row the link wraps whole onto its
            // own line, where a leading or trailing dot would dangle.
            <Stack
              direction="row"
              gap={0}
              align="baseline"
              className="mt-0.5 flex-wrap gap-x-3"
            >
              <ConfidenceLabel value={action.confidence} />
              {fix}
            </Stack>
          )}
          <StChangesNote action={action} />
          {blockedReason && (
            <Text
              id={blockedReasonId}
              variant="caption"
              tone="destructive"
              className="mt-0.5"
            >
              {blockedReason}
            </Text>
          )}
        </Stack>
        {/* Dismiss as an icon keeps both row shapes nearly the same width, so
            flushing them right lines the pills up without a dead gap. */}
        <Stack
          direction="row"
          gap={2}
          align="center"
          justify="start"
          className="flex-none sm:justify-end"
        >
          {/* Both shapes get both controls. On a flag the two are not the same
              act: approving records it acknowledged, rejecting records it
              dismissed, and ActionStatusGlyph renders those differently. One
              min-width keeps every pill on the same two edges. */}
          <Button
            variant={noOp ? "outline" : "secondary"}
            className="h-9 min-w-30 sm:h-8"
            onClick={() => onResolve("approved")}
            disabled={resolving || Boolean(blockedReason)}
            aria-busy={resolving || undefined}
            aria-describedby={blockedReason ? blockedReasonId : undefined}
          >
            {noOp ? "Mark as seen" : approveLabel(action, runActions)}
          </Button>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  color="danger"
                  size="icon"
                  aria-label={noOp ? "Ignore" : "Dismiss"}
                  onClick={() => onResolve("rejected")}
                  disabled={resolving}
                  className={DISMISS_ICON_BUTTON}
                />
              }
            >
              <XIcon aria-hidden />
            </TooltipTrigger>
            <TooltipContent>{noOp ? "Ignore" : "Dismiss"}</TooltipContent>
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
  );
};
